<template>
    <div class="available-config">
        <div class="d-flex flex-column">
            <h4 class="mt-2">{{ $t("fastWizard.channelAvailability") }}</h4>
            <div class="hourContainer">
                <div class="hourItem" :class="{ selected: mode === '7/24' }" @click="mode = '7/24'">
                    <md-radio v-model="mode" value="7/24">{{ $t("fastWizard.availability724") }}</md-radio>
                </div>
                <div
                    class="hourItem"
                    :class="{ selected: mode === 'agent-status-based' }"
                    @click="mode = 'agent-status-based'"
                >
                    <md-radio v-model="mode" value="agent-status-based">
                        {{ $t("fastWizard.basedOnTeam") }}
                    </md-radio>
                    <span class="subtitle">{{ $t("fastWizard.basedOnTeamExplanation") }}</span>
                </div>
                <div class="hourItem" :class="{ selected: mode === 'custom' }" @click="mode = 'custom'">
                    <md-radio v-model="mode" value="custom">
                        {{ $t("fastWizard.customHours") }}
                    </md-radio>
                    <span class="subtitle private-time">{{ $t("fastWizard.customHoursExplanation") }}</span>
                    <div class="rules" v-if="mode === 'custom'">
                        <div>
                            <span>{{ $t("fastWizard.customHoursTimezone") }}</span>
                            <a-select
                                show-search
                                placeholder="Select Timezone"
                                option-filter-prop="children"
                                style="width: 250px"
                                :filter-option="filterOption"
                                @change="changeTimezone"
                                :value="timezone"
                            >
                                <a-select-option :value="tz" v-for="(tz, i) in timezones" :key="i">
                                    {{ tz }}
                                </a-select-option>
                            </a-select>
                        </div>
                        <div v-for="rule in rules" class="ruleItem">
                            <a-select
                                show-search
                                placeholder="Select Day(s)"
                                option-filter-prop="children"
                                style="width: 120px"
                                :filter-option="filterOption"
                                @change="(e) => handleDayChange(e, rule._id)"
                                :value="daysHashcode(rule.days)"
                            >
                                <a-select-option :value="daysHashcode(day.value)" v-for="(day, i) in days" :key="i">
                                    {{ day.label[$i18n.locale] }}
                                </a-select-option>
                            </a-select>
                            <span>{{ $t("fastWizard.customHoursFrom") }}</span>
                            <a-select
                                show-search
                                placeholder="Select Start Hour"
                                option-filter-prop="children"
                                style="width: 80px; margin-left: 10px"
                                :filter-option="filterOption"
                                @change="(e) => handleFromHourChange(e, rule._id)"
                                :value="rule.from"
                            >
                                <a-select-option :value="hour" v-for="(hour, i) in hours" :key="i">
                                    {{ hour }}
                                </a-select-option>
                            </a-select>
                            <span>{{ $t("fastWizard.customHoursTo") }}</span>
                            <a-select
                                show-search
                                placeholder="Select End Hour"
                                option-filter-prop="children"
                                style="width: 80px; margin-left: 10px"
                                :filter-option="filterOption"
                                @change="(e) => handleEndHourChange(e, rule._id)"
                                :value="rule.to"
                            >
                                <a-select-option :value="hour" v-for="(hour, i) in hours" :key="i">
                                    {{ hour }}
                                </a-select-option>
                            </a-select>
                            <a-button
                                class="btnDelete d-flex align-items-center px-1 ml-2"
                                @click="deleteHour(rule._id)"
                                :disabled="rules.length === 1"
                            >
                                <ActionDelete />
                            </a-button>
                        </div>
                        <a-button class="btnAddHour" type="primary" ghost @click="addHour">
                            {{ $t("fastWizard.customHoursAddHours") }}
                        </a-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../utils/log2";
import { mapActions, mapGetters, mapState } from "vuex";
import { sendSocketMessage } from "../../utils/socketStream";

import _ from "lodash";
import moment from "moment-timezone";
import ActionDelete from "../svg/ActionDelete";
import { getNavigatorTimezone } from "../../utils/language";

const log = log2("AvailabilityConfig");
const defaultRules = [
    {
        days: [1, 2, 3, 4, 5],
        from: "09:00",
        to: "17:00",
    },
];
const momentTimezoneList = moment.tz.names();
const defaultTZ =
    momentTimezoneList.find(
        (x) => x.trim().toLowerCase().indexOf(getNavigatorTimezone().timeZone.trim().toLowerCase()) !== -1
    ) || "UTC";
log("defaultTZ", defaultTZ, momentTimezoneList, getNavigatorTimezone());
export default {
    name: "AvailabilityConfig",
    components: { ActionDelete },
    data() {
        return {
            mode: "agent-status-based",
            timezone: null,
            rules: defaultRules,
            timezones: momentTimezoneList,
            hours: [
                ...[...Array(48).keys()].map((v, i) =>
                    moment()
                        .startOf("day")
                        .add(v * 30, "minute")
                        .format("HH:mm")
                ),
                "23:59",
            ],
            days: [
                {
                    label: {
                        en: "Every Day",
                        ru: "Every Day",
                        tr: "Her Gün",
                        de: "Täglich",
                        fr: "Tous les jours",
                        es: "Cada día",
                        ar: "كل يوم",
                    },
                    value: [1, 2, 3, 4, 5, 6, 7],
                },
                {
                    label: {
                        en: "Mon - Fri",
                        ru: "Mon - Fri",
                        tr: "Pzt - Cuma",
                        de: "Mo - Freitag",
                        fr: "Lun - Vendredi",
                        es: "lun - viernes",
                        ar: "الاثنين - الجمعة",
                    },
                    value: [1, 2, 3, 4, 5],
                },
                {
                    label: {
                        en: "Sat - Sun",
                        ru: "Sat - Sun",
                        tr: "Cts - Pzr",
                        de: "Sa - So",
                        fr: "Sam-Dim",
                        es: "Sáb-dom",
                        ar: "اشعة الشمس",
                    },
                    value: [6, 7],
                },
                {
                    label: {
                        en: "Monday",
                        ru: "Monday",
                        tr: "Pazartesi",
                        de: "Montag",
                        fr: "Lundi",
                        es: "Lunes",
                        ar: "الاثنين",
                    },
                    value: [1],
                },
                {
                    label: {
                        en: "Tuesday",
                        ru: "Tuesday",
                        tr: "Salı",
                        de: "Dienstag",
                        fr: "Mardi",
                        es: "Martes",
                        ar: "يوم الثلاثاء",
                    },
                    value: [2],
                },
                {
                    label: {
                        en: "Wednesday",
                        ru: "Wednesday",
                        tr: "Çarşamba",
                        de: "Mittwoch",
                        fr: "Mercredi",
                        es: "Miércoles",
                        ar: "الاربعاء",
                    },
                    value: [3],
                },
                {
                    label: {
                        en: "Thursday",
                        ru: "Thursday",
                        tr: "Perşembe",
                        de: "Donnerstag",
                        fr: "Jeudi",
                        es: "Jueves",
                        ar: "يوم الخميس",
                    },
                    value: [4],
                },
                {
                    label: {
                        en: "Friday",
                        ru: "Friday",
                        tr: "Cuma",
                        de: "Freitag",
                        fr: "Vendredi",
                        es: "Viernes",
                        ar: "جمعة",
                    },
                    value: [5],
                },
                {
                    label: {
                        en: "Saturday",
                        ru: "Saturday",
                        tr: "Cumartesi",
                        de: "Samstag",
                        fr: "Samedi",
                        es: "Sábado",
                        ar: "السبت",
                    },
                    value: [6],
                },
                {
                    label: {
                        en: "Sunday",
                        ru: "Sunday",
                        tr: "Pazar",
                        de: "Sonntag",
                        fr: "Dimanche",
                        es: "Domingo",
                        ar: "الأحد",
                    },
                    value: [7],
                },
            ],
        };
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            settingInitialized: (state) => state.setting.initialized,
        }),
    },
    methods: {
        ...mapActions({
            getSettings: "setting/getSettings",
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),

        async deleteHour(id) {
            log("deleteHour", id);
            const newHours = this.rules.filter((x) => x._id !== id);
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async addHour() {
            const newHours = [...this.rules, defaultRules[0]];
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async changeTimezone(timezone) {
            log("changeTimezone", { timezone });

            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.timezone",
                value: timezone,
            });
            this.applySettings(newSettings);
        },
        async handleDayChange(value, ruleId) {
            log("handleDayChange", { value, ruleId });
            const newHours = this.rules.map((x) => {
                return x._id === ruleId
                    ? {
                          ...x,
                          days: this.days.find((day) => this.daysHashcode(day.value) === value).value,
                      }
                    : x;
            });
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async handleFromHourChange(value, ruleId) {
            log("handleFromHourChange", { value, ruleId });
            const newHours = this.rules.map((x) => {
                return x._id === ruleId ? { ...x, from: value } : x;
            });
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        async handleEndHourChange(value, ruleId) {
            log("handleEndHourChange", { value, ruleId });
            const newHours = this.rules.map((x) => {
                return x._id === ruleId ? { ...x, to: value } : x;
            });
            let newSettings = await this.setSettingValue({
                setting: "availabilitySettings.hours",
                value: newHours,
            });
            this.applySettings(newSettings);
        },
        handleBlur() {
            log("blur");
        },
        handleFocus() {
            log("focus");
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        daysHashcode(days) {
            return _.orderBy(days, (q) => q, "asc").join("-");
        },
        applySettings(newSettings) {
            log("applySettings", newSettings);
            this.updateSettings(newSettings).then(() => {
                sendSocketMessage("user settings-updated", {
                    siteId: this.site._id,
                });
            });
        },
    },
    watch: {
        settings: {
            handler: function (newSettings) {
                if (_.get(newSettings, "availabilitySettings.mode") !== this.mode) {
                    this.mode = _.get(newSettings, "availabilitySettings.mode");
                }
                if (_.get(newSettings, "availabilitySettings.mode") === "custom") {
                    if (!_.isEqual(this.rules, newSettings.availabilitySettings.hours)) {
                        this.rules = newSettings.availabilitySettings.hours;
                    }
                }
                const tz = _.get(newSettings, "availabilitySettings.timezone");
                if (tz && tz !== this.timezone) {
                    this.timezone = tz;
                }
            },
            deep: true,
        },
        async mode(newMode, oldMode) {
            if (newMode && this.settings) {
                const oldSettings = _.cloneDeep(this.settings);
                const updates = [
                    {
                        setting: "availabilitySettings.mode",
                        value: newMode,
                    },
                ];
                if (newMode === "custom") {
                    if (!oldSettings.availabilitySettings.hours.length) {
                        updates.push({
                            setting: "availabilitySettings.hours",
                            value: defaultRules,
                        });
                    }
                    if (!oldSettings.timezone || oldSettings.timezone === "UTC") {
                        updates.push({
                            setting: "availabilitySettings.timezone",
                            value: defaultTZ,
                        });
                    }
                }
                const newSettings = await this.setSettingValue(updates);
                if (!_.isEqual(oldSettings, newSettings)) this.applySettings(newSettings);
            }
        },
    },
    mounted() {
        this.mode = _.get(this.settings, "availabilitySettings.mode", "agent-status-based");
        this.timezone = _.get(
            this.settings,
            "availabilitySettings.timezone",
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        if (_.get(this.settings, "availabilitySettings.mode") === "custom") {
            this.rules = this.settings.availabilitySettings.hours;
        }
    },
};
</script>

<style lang="scss"></style>
